"use client";

import React, { useState, useEffect } from "react";

const DEFAULT_PROCESS_STEPS = [
  {
    id: "planningConsultation",
    label: "1. Planning & Consultation",
    title: "Planning & Consultation",
    desc: "Our journey begins with understanding your vision. Based on your vision, we collaboratively create a project roadmap with clear milestones. We’ll consult about your ideas, target audience, and desired outcomes.",
    points: [
      { title: "Vision and Goals Discussion", desc: "Initiate discussions to understand your objectives, target audience, and desired outcomes." },
      { title: "Project Roadmap Creation", desc: "Collaboratively create a detailed project plan with clear milestones and deadlines." },
      { title: "Scope Definition", desc: "Outline the key features, functionalities, and requirements." },
      { title: "Resource Allocation", desc: "Allocate team roles, tools, and budgets to ensure effective project management." }
    ]
  },
  {
    id: "design",
    label: "2. Design",
    title: "Design",
    desc: "Our skilled designers translate your vision into a user-friendly and visually appealing solution. They focus on creating a positive user experience that keeps users engaged and coming back for more.",
    points: [
      { title: "Wireframing", desc: "Develop wireframes to outline the structure and layout." },
      { title: "UI/UX Design", desc: "Craft the user interface and user experience, focusing on aesthetics and functionality." },
      { title: "Prototype Creation", desc: "Create interactive prototypes for user feedback and refinement." },
      { title: "Design Approval", desc: "Present the design for your review and approval, making necessary adjustments based on feedback." }
    ]
  },
  {
    id: "development",
    label: "3. Development",
    title: "Development",
    desc: "Our expert developers take your design and turn it into reality. We keep you informed throughout the process with regular progress reports and milestone deliverables. This ensures you have complete transparency and control over the development phase.",
    points: [
      { title: "Frontend Development", desc: "Code the client-side interface, ensuring responsiveness and compatibility." },
      { title: "Backend Development", desc: "Build the server-side logic, integration, and functionalities." },
      { title: "Regular Updates", desc: "Provide progress reports and milestone deliverables to keep you informed." },
      { title: "Quality Assurance", desc: "Conduct initial testing and debugging to ensure the development functions as intended." }
    ]
  },
  {
    id: "testing",
    label: "4. Testing",
    title: "Testing",
    desc: "Before launch, our dedicated QA specialists meticulously test every aspect of your product. They identify and fix any potential issues to guarantee a smooth and flawless user experience.",
    points: [
      { title: "Functionality Testing", desc: "Test all features and functionalities to ensure they work correctly." },
      { title: "Usability Testing", desc: "Evaluate the user interface and user experience for ease of use and navigation." },
      { title: "Performance Testing", desc: "Assess the speed, load time, and responsiveness under various conditions." },
      { title: "Security Testing", desc: "Conduct thorough security checks to identify and fix vulnerabilities." }
    ]
  },
  {
    id: "deployment",
    label: "5. Deployment",
    title: "Deployment",
    desc: "Once everything is finalised, our team strategically chooses the most suitable deployment methods and services. Now, your project is live for everyone to see and interact with!",
    points: [
      { title: "Hosting Selection", desc: "Recommend and set up the most suitable services based on your needs." },
      { title: "Final Preparations", desc: "Perform final checks and configurations before going live." },
      { title: "Launch Strategy", desc: "Develop a launch plan, including marketing and promotional strategies." },
      { title: "Go Live", desc: "Deploy the product, ensuring everything is functional and optimised for performance." }
    ]
  },
  {
    id: "maintenance",
    label: "6. Maintenance",
    title: "Maintenance",
    desc: "Our commitment doesn't end at launch. We provide ongoing support to ensure your product stays up-to-date, secure, and bug-free.",
    points: [
      { title: "Regular Updates", desc: "Implement software and security updates to keep the website current." },
      { title: "Monitoring", desc: "Continuously monitor performance and security to prevent issues." },
      { title: "Bug Fixes", desc: "Address any technical issues or bugs that arise post-launch." },
      { title: "Performance Optimization", desc: "Optimise speed and functionality based on user feedback and analytics." }
    ]
  }
];

export default function OurProcess({ development }) {

  const tabs = development?.tabs;
  let processSteps = DEFAULT_PROCESS_STEPS;

  if (Array.isArray(tabs) && tabs.length > 0) {
    processSteps = tabs.map((tab, idx) => ({
      id: tab._id || `step-${idx}`,
      label: `${tab.stepNumber || (idx + 1)}. ${tab.tabTitle || "Step"}`,
      title: tab.leftCard?.title || tab.tabTitle || "Process Step",
      desc: tab.leftCard?.description || tab.leftCard?.desc || "",
      points: (tab.rightCards || []).map(rc => ({
        title: rc.title || "Point",
        desc: rc.description || rc.desc || ""
      }))
    }));
  }

  const [activeTab, setActiveTab] = useState("");

  useEffect(() => {
    if (processSteps.length > 0) {
      if (!activeTab || !processSteps.find(s => s.id === activeTab)) {
        setActiveTab(processSteps[0].id);
      }
    }
  }, [processSteps, activeTab]);

  const currentStep = processSteps.find((step) => step.id === activeTab) || processSteps[0];

  if (!currentStep) return null;

  return (
    <section className="py-16 md:py-24 relative z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-xs font-bold tracking-widest text-[#f48227] uppercase bg-transparent px-4 py-1.5 rounded-full" style={{ border: "1px solid #f48227" }}>
            Our Process
          </span>
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-white mt-4 mb-2">
            {development?.title || "Our Agile Development Process"}
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto text-sm sm:text-base">
            {development?.subtitle || "Companies employ developers from us because we have a proven track record of delivering high-quality projects on time."}
          </p>
        </div>

        {/* Tabs Header */}
        <div className="mb-12 overflow-x-auto flex whitespace-nowrap justify-start md:justify-center items-center pb-4 scrollbar-hide">
          <div className="flex gap-2 md:gap-3 p-1.5 bg-white/5 backdrop-blur-md ring-2 ring-zinc-800 rounded-full w-fit">
            {processSteps.map((step) => (
              <button
                key={step.id}
                onClick={() => setActiveTab(step.id)}
                className={`px-4 md:px-6 py-2.5 text-xs md:text-sm font-bold rounded-full transition-all duration-300 ${activeTab === step.id
                  ? "bg-[#f1833b] text-white shadow-lg shadow-orange-500/25"
                  : "text-gray-300 hover:text-white hover:bg-white/5"
                  }`}
              >
                {step.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch transition-all duration-500">
          {/* Left Column */}
          <div className="lg:col-span-4 bg-gradient-to-b from-white/10 to-white/5 ring-2 ring-zinc-800 p-8 rounded-3xl backdrop-blur-lg flex flex-col justify-center shadow-xl shadow-black/20">
            <div className="w-12 h-12 rounded-2xl bg-[#f1833b]/20 flex items-center justify-center text-2xl mb-6 text-[#f1833b] font-bold border border-[#f1833b]/10">
              {processSteps.findIndex(s => s.id === activeTab) + 1}
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">
              {currentStep.title}
            </h3>
            <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
              {currentStep.desc}
            </p>
          </div>

          {/* Right Column - grid */}
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {currentStep.points.map((point, index) => (
              <div
                key={index}
                className="p-6 bg-[#121212]/60 ring-2 ring-zinc-800 rounded-2xl hover:ring-[#f1833b]/40 transition-all duration-300 hover:bg-[#161616]/80 group hover:-translate-y-1 shadow-md hover:shadow-[0_0_30px_rgba(241,131,59,0.05)] flex flex-col justify-between"
              >
                <div>
                  <div className="text-base font-bold text-white mb-2 flex items-center gap-3 group-hover:text-[#f1833b] transition-colors duration-300">
                    <span className="w-2 h-2 bg-[#f1833b] rounded-full block shadow-lg shadow-[#f1833b]/50 group-hover:scale-125 transition-transform duration-300"></span>
                    {point.title}
                  </div>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {point.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
